/** Offline outbox (§5.8, §7): voice notes queue on the device and process on reconnect.
 *  Nothing pretends to be transcribed before it is; states are visible in field mode. */
import { createRecord, getRecord, listLive, putRecord } from '../core/db';
import { bufToB64 } from '../core/db';
import { nowIso } from '../core/ids';
import { aiConfigured } from './client';
import { fieldNotes } from './tasks';
import type { OutboxItem } from '../core/types';

export interface VoicePayload { mime: string; b64: string; recordedAt: string }

export async function queueVoiceNote(jobId: string, blob: Blob): Promise<OutboxItem> {
  const b64 = bufToB64(await blob.arrayBuffer());
  return createRecord('outbox', {
    kind: 'transcription', jobId,
    payload: { mime: blob.type || 'audio/webm', b64, recordedAt: nowIso() } satisfies VoicePayload,
    state: 'queued', attempts: 0,
  });
}

export interface OutboxRun { processed: number; failed: number; skipped: number }

/** Runs on boot, on 'online', and after recording while online. Concurrent triggers
 *  coalesce into one drain — the browser fires its own 'online' alongside ours. */
let draining: Promise<OutboxRun> | null = null;
export function processOutbox(user: string): Promise<OutboxRun> {
  draining ??= drainOnce(user).finally(() => { draining = null; });
  return draining;
}

async function drainOnce(user: string): Promise<OutboxRun> {
  const run: OutboxRun = { processed: 0, failed: 0, skipped: 0 };
  if (!navigator.onLine || !(await aiConfigured())) { run.skipped++; return run; }
  const items = (await listLive('outbox')).filter((o) => o.state === 'queued' || (o.state === 'failed' && o.attempts < 3));
  for (const item of items) {
    if (item.kind !== 'transcription') continue;
    const fresh = await getRecord('outbox', item.id);
    if (!fresh || fresh.deletedAt || fresh.state === 'inflight' || fresh.state === 'done') continue;
    const inflight = await putRecord('outbox', { ...fresh, state: 'inflight' });
    try {
      const p = fresh.payload as VoicePayload;
      const r = await fieldNotes({ mime: p.mime, b64: p.b64 });
      // structured results land in the review queue — the human still accepts each row (§5.10)
      for (const it of r.data.inventory) {
        await createRecord('reviewQueue', {
          feature: 'field_notes', jobId: fresh.jobId,
          payload: { kind: 'inventory', ...it },
          confidence: it.confidence, costUsd: r.costUsd, status: 'pending',
        }, { snapshot: false });
      }
      for (const v of r.data.verifications) {
        await createRecord('reviewQueue', {
          feature: 'field_notes', jobId: fresh.jobId,
          payload: { kind: 'note', lineHint: v.lineHint, note: v.note, transcript: r.data.transcript },
          confidence: 'medium', costUsd: 0, status: 'pending',
        }, { snapshot: false });
      }
      await putRecord('outbox', { ...inflight, state: 'done' });
      run.processed++;
    } catch (e) {
      await putRecord('outbox', {
        ...inflight, state: 'failed', attempts: inflight.attempts + 1,
        lastError: (e as Error).message,
      });
      run.failed++;
    }
  }
  void user;
  return run;
}

export async function pendingVoiceCount(jobId: string): Promise<number> {
  return (await listLive('outbox')).filter((o) => o.jobId === jobId && o.kind === 'transcription' && (o.state === 'queued' || o.state === 'failed' || o.state === 'inflight')).length;
}
